import axios from "axios";
import router from "@/router";

const API_URL = process.env.VUE_APP_API_URL;
const PERSIST_KEY = "mm:ps";

const api = axios.create({
  baseURL: API_URL + "/api",
  withCredentials: true,
});

let refreshPromise = null;

function isAuthApiRoute(url = "") {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/logout")
  );
}

function isResetValidateRoute(url = "") {
  return url.includes("/auth/reset/validate");
}

function routeRequiresAuthSafely() {
  try {
    const fullPath =
      window.location.pathname + window.location.search + window.location.hash;

    const resolved = router.resolve(fullPath);
    return (resolved.matched || []).some((m) => m.meta?.requiresAuth === true);
  } catch (e) {
    return true;
  }
}

function readPersistedState() {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function writePersistedState(nextState) {
  try {
    localStorage.setItem(PERSIST_KEY, JSON.stringify(nextState));
  } catch (e) {
    console.warn("Não foi possível atualizar o estado persistido:", e);
  }
}

function shouldTryRefreshFromStorage() {
  const persisted = readPersistedState();
  return persisted?.shouldTryRefresh === true;
}

function setShouldTryRefreshInStorage(value) {
  const persisted = readPersistedState();

  writePersistedState({
    ...persisted,
    shouldTryRefresh: !!value,
  });
}

export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post("/auth/refresh", null).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

function shouldAttemptRefresh() {
  return routeRequiresAuthSafely() || shouldTryRefreshFromStorage();
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;

    if (!response || !config) throw error;
    if (response.status !== 401) throw error;

    if (config._retry) throw error;
    if (isAuthApiRoute(config.url || "")) throw error;
    if (isResetValidateRoute(config.url || "")) throw error;

    if (!shouldAttemptRefresh()) {
      throw error;
    }

    config._retry = true;

    try {
      await refreshSession();
      setShouldTryRefreshInStorage(true);
      return api(config);
    } catch (e) {
      setShouldTryRefreshInStorage(false);
      window.dispatchEvent(new Event("mm:auth:logout"));
      throw error;
    }
  },
);

export default api;
